import { useState, useCallback, useMemo, useEffect } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { signIn } from "next-auth/react";
import { PhoneNumber } from "cy-ui/components/PhoneNumber";
import { LoginRequest } from "../models";
import { TabType } from "../SignInForm";
import { sleep } from "cy-util";
import { createLocalStorageService } from "../utils/localStorage";

export interface SignInFormState {
  submitting: boolean;
  selectedTab: TabType;
  userId: string;
  password: string;
  mobile: PhoneNumber;
  error?: string;
}

export interface SignInFormActions {
  setSelectedTab: (tab: TabType) => void;
  setUserId: (userId: string) => void;
  setPassword: (password: string) => void;
  setError: (error?: string) => void;
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => Promise<void>;
  mobileChangeHandler: (newMobile: PhoneNumber) => void;
}

export interface SignInFormDependencies {
  signIn?: typeof signIn;
  router?: ReturnType<typeof useRouter>;
  localStorage?: Pick<Storage, "getItem" | "setItem">;
  sleep?: typeof sleep;
}

export function useSignInForm(
  defaultPhoneIsd: string = "27",
  dependencies: SignInFormDependencies = {},
): SignInFormState & SignInFormActions {
  const defaultRouter = useRouter();
  const {
    signIn: signInFn = signIn,
    router = defaultRouter,
    localStorage = typeof window !== "undefined"
      ? window.localStorage
      : undefined,
    sleep: sleepFn = sleep,
  } = dependencies;

  const searchParams = useSearchParams();

  const [submitting, setSubmitting] = useState(false);
  const [selectedTab, setSelectedTab] = useState<TabType>(TabType.Phone);
  const [userId, setUserId] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [mobile, setMobile] = useState<PhoneNumber>({
    isd: "",
    number: "",
  });
  const [error, setError] = useState<string>();

  // Initialize mobile ISD from localStorage on mount
  useEffect(() => {
    if (localStorage) {
      const storageService = createLocalStorageService(localStorage);
      const savedIsd = storageService.getLastUsedPhoneIsd();
      const defaultIsd = savedIsd || defaultPhoneIsd;

      setMobile((prev) =>
        defaultIsd !== prev.isd ? { ...prev, isd: defaultIsd } : prev,
      );
    }
  }, [defaultPhoneIsd, localStorage]);

  const mobileChangeHandler = useCallback(
    (newMobile: PhoneNumber) => {
      // Validate ISD format before saving to localStorage
      const validIsdRegex = /^\+?[1-9]\d{0,2}$/;
      const cleanedIsd = newMobile.isd.replace("+", "");
      if (localStorage) {
        if (validIsdRegex.test(cleanedIsd)) {
          localStorage.setItem("signin:lastUsedCountryCode", newMobile.isd);
        } else {
          console.warn(`Invalid Isd ${newMobile.isd}`);
        }
      }
      setMobile(newMobile);
    },
    [localStorage],
  );

  // Memoize callback URL calculation for performance
  const callbackUrl = useMemo(() => {
    if (typeof window === "undefined") {
      return "/";
    }
    const origin = `${window.location.origin.toLowerCase()}/`;
    const defaultCallbackUrl = `${origin}home`.toLowerCase();
    const paramsCallbackUrl = searchParams?.get("callbackUrl")?.toLowerCase();

    return paramsCallbackUrl && paramsCallbackUrl !== origin
      ? paramsCallbackUrl
      : defaultCallbackUrl;
  }, [searchParams]);

  const validateForm = useCallback((): string | null => {
    if (selectedTab === TabType.Phone) {
      if (!mobile.number || mobile.number.trim() === "") {
        return "Please enter a valid phone number";
      }
      if (!mobile.isd || mobile.isd.trim() === "") {
        return "Please select a country code";
      }
    } else {
      if (!userId || userId.trim() === "") {
        return "Please enter your customer ID or email";
      }
    }

    if (!password || password.trim() === "") {
      return "Please enter your password";
    }

    return null;
  }, [selectedTab, mobile, userId, password]);

  const createLoginRequest = useCallback((): LoginRequest => {
    const userIdentifier =
      selectedTab === TabType.UserId
        ? { userId: userId.trim() }
        : {
            mobile_isd: mobile.isd.trim(),
            mobile_number: mobile.number.trim(),
          };

    return { password, ...userIdentifier };
  }, [selectedTab, userId, mobile, password]);

  const onSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      try {
        setSubmitting(true);
        setError("");
        e.preventDefault();

        // Form validation
        const validationError = validateForm();
        if (validationError) {
          setError(validationError);
          return;
        }

        const loginRequest = createLoginRequest();
        const res = await signInFn("credentials", {
          redirect: false,
          ...loginRequest,
          callbackUrl,
        });

        if (!res?.ok) {
          setError("Your login credentials were invalid");
        } else {
          if (res.url == null) {
            throw new Error("res.url is null");
          }
          router.push(res.url);
          // Add back the sleep for timing/UX reasons
          await sleepFn(2000);
        }
      } catch (err) {
        console.error("SignIn error:", err);
        if (err instanceof Error && err.message === "res.url is null") {
          setError(
            "Authentication succeeded but redirect failed. Please refresh the page.",
          );
        } else {
          setError("Something went wrong. Please try again.");
        }
      } finally {
        setSubmitting(false);
      }
    },
    [validateForm, createLoginRequest, signInFn, callbackUrl, router, sleepFn],
  );

  return {
    // State
    submitting,
    selectedTab,
    userId,
    password,
    mobile,
    error,
    // Actions
    setSelectedTab,
    setUserId,
    setPassword,
    setError,
    onSubmit,
    mobileChangeHandler,
  };
}
